/**
 * Input - Themed text input with label, helper text, error state, and optional
 * leading / trailing icon slots. Matches the Game UI Kit Input Fields section.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type InputType = 'text' | 'password' | 'email' | 'search' | 'number' | 'tel' | 'url';

export interface InputOptions extends BaseUIComponentOptions {
  /** Label rendered above the field. */
  label?: string;
  /** Placeholder shown when empty. */
  placeholder?: string;
  /** Initial value. */
  value?: string;
  /** Helper / error text rendered beneath the field. */
  helper?: string;
  /** HTML input type. */
  type?: InputType;
  /** Toggle the error state (red border + helper text). */
  error?: boolean;
  /** Toggle disabled state. */
  disabled?: boolean;
  /** Inline HTML for the leading icon slot (e.g. an SVG). */
  leadingIconHtml?: string;
  /** Inline HTML for the trailing icon slot (e.g. an SVG). */
  trailingIconHtml?: string;
  /** Called when the value changes. */
  onChange?: (value: string, event: Event) => void;
  /** Called when the input loses focus. */
  onBlur?: (event: FocusEvent) => void;
}

export class Input extends BaseUIComponent<InputOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Input',
    category: 'input',
    summary: 'Text input with label, helper / error text, and optional icons.',
    useCases: ['form field', 'username', 'email', 'search', 'password', 'chat composer'],
    optionsType: 'InputOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/input.html',
      styles: '@engine/assets/ui/styles/input.css',
    },
  };

  private containerElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private helperElement: HTMLElement | null = null;
  private leadingIconElement: HTMLElement | null = null;
  private trailingIconElement: HTMLElement | null = null;
  private changeHandler: ((event: Event) => void) | null = null;
  private blurHandler: ((event: FocusEvent) => void) | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Input.metadata.assetPaths.template,
      stylesPath: Input.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<InputOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      label: '',
      placeholder: '',
      value: '',
      helper: '',
      type: 'text',
      error: false,
      disabled: false,
      leadingIconHtml: '',
      trailingIconHtml: '',
      onChange: () => { /* no-op */ },
      onBlur: () => { /* no-op */ },
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      label: this.options.label,
      placeholder: this.options.placeholder,
      value: this.options.value,
      helper: this.options.helper,
      type: this.options.type,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.containerElement = this.layout.querySelector('.ui-input-container') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-input-label]') as HTMLElement | null;
    this.inputElement = this.layout.querySelector('[data-input]') as HTMLInputElement | null;
    this.helperElement = this.layout.querySelector('[data-input-helper]') as HTMLElement | null;
    this.leadingIconElement = this.layout.querySelector('[data-input-icon-leading]') as HTMLElement | null;
    this.trailingIconElement = this.layout.querySelector('[data-input-icon-trailing]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.applyError();
    this.applyDisabled();
    this.applyIcons();
    this.attachListeners();
  }

  private attachListeners(): void {
    if (!this.inputElement) return;
    this.changeHandler = (event: Event) => {
      const target = event.target as HTMLInputElement;
      this.options.value = target.value;
      this.options.onChange(target.value, event);
    };
    this.blurHandler = (event: FocusEvent) => this.options.onBlur(event);
    this.inputElement.addEventListener('input', this.changeHandler);
    this.inputElement.addEventListener('blur', this.blurHandler);
  }

  /** Set the value programmatically. */
  public setValue(value: string): void {
    this.options.value = value;
    if (this.inputElement) this.inputElement.value = value;
  }

  /** Get the current value. */
  public getValue(): string {
    return this.inputElement?.value ?? this.options.value;
  }

  /** Update the label text. */
  public setLabel(label: string): void {
    this.options.label = label;
    if (this.labelElement) this.labelElement.textContent = label;
  }

  /** Update the helper / error text. */
  public setHelper(helper: string): void {
    this.options.helper = helper;
    if (this.helperElement) this.helperElement.textContent = helper;
  }

  /** Toggle the error state. */
  public setError(error: boolean): void {
    this.options.error = error;
    this.applyError();
  }

  /** Toggle disabled state. */
  public setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.applyDisabled();
  }

  /** Replace the leading icon HTML. */
  public setLeadingIconHtml(html: string): void {
    this.options.leadingIconHtml = html;
    this.applyIcons();
  }

  /** Replace the trailing icon HTML. */
  public setTrailingIconHtml(html: string): void {
    this.options.trailingIconHtml = html;
    this.applyIcons();
  }

  private applyError(): void {
    if (!this.containerElement) return;
    this.containerElement.dataset.error = this.options.error ? 'true' : 'false';
  }

  private applyDisabled(): void {
    if (!this.inputElement) return;
    this.inputElement.disabled = this.options.disabled;
  }

  private applyIcons(): void {
    if (!this.containerElement) return;
    if (this.leadingIconElement) this.leadingIconElement.innerHTML = this.options.leadingIconHtml;
    if (this.trailingIconElement) this.trailingIconElement.innerHTML = this.options.trailingIconHtml;
    this.containerElement.dataset.leadingIcon = this.options.leadingIconHtml ? 'true' : 'false';
    this.containerElement.dataset.trailingIcon = this.options.trailingIconHtml ? 'true' : 'false';
  }

  protected override onDestroy(): void {
    if (this.inputElement) {
      if (this.changeHandler) this.inputElement.removeEventListener('input', this.changeHandler);
      if (this.blurHandler) this.inputElement.removeEventListener('blur', this.blurHandler);
    }
    this.changeHandler = null;
    this.blurHandler = null;
    this.containerElement = null;
    this.labelElement = null;
    this.inputElement = null;
    this.helperElement = null;
    this.leadingIconElement = null;
    this.trailingIconElement = null;
  }
}
